import { SohoMessageRef } from 'ids-enterprise-ng';
import { LMContextService, LMModalOpenService } from '@app/services';

/**
 * Wrapper for the jQuery modal control.
 */
export class LMMessageWrapperDialogRef {

  private messageDialog: SohoMessageRef;
  private _options: SohoMessageOptions = {};
  private _dialogResult: any;

  private beforeCloseMethod = (): boolean => {
    if (this.lmContextService.isUnified()) {
      this.lmModalOpenService.removeModal(this.uuid);
    }
    return true;
  };

  /**
   * Sets the whole options block for this modal dialog.
   *
   * @param options - the options to set.
   */
  options(options?: SohoMessageOptions): LMMessageWrapperDialogRef {
    this._options = options || {};
    return this;
  }

  /**
   * Sets the title of the message dialog.
   *
   * @param title - the title of the dialog.
   */
  title(title: string): LMMessageWrapperDialogRef {
    this._options.title = title;
    return this;
  }

  /**
   * Sets the buttons to use on the modal dialog.
   *
   * @param buttons - list of buttons to display
   */
  buttons(buttons: SohoModalButton[]): LMMessageWrapperDialogRef {
    this._options.buttons = buttons;
    return this;
  }

  /**
   * Is this an error dialog?
   *
   * @param isError - true if this dialog is to be styled an an alert.
   */
  isError(isError: boolean): LMMessageWrapperDialogRef {
    this._options.isError = isError;
    return this;
  }


  /**
   * Sets the 'message' that the message dialog displays.
   *
   * @param message - a selector or string representing the dialog content.
   */
  message(message: string): LMMessageWrapperDialogRef {
    this._options.message = message;
    return this;
  }

  /**
   * Sets the 'status' of the modal control.
   */
  status(status: string): LMMessageWrapperDialogRef {
    this._options.status = status;
    return this;
  }

  /**
   * Sets the 'cssClass' of the modal control.
   */
  cssClass(cssClass: string): LMMessageWrapperDialogRef {
    this._options.cssClass = cssClass;
    return this;
  }

  /**
   * If true, it will show a close icon button on the top right of the message.
   *
   * @param showCloseBtn - if true, the x button will be shown.
   */
  showCloseBtn(showCloseBtn: boolean): LMMessageWrapperDialogRef {
    this._options.showCloseBtn = showCloseBtn;
    return this;
  }

  /**
   * Sets the 'allowTags' of the modal control.
   */
  allowedTags(allowedTags: string): LMMessageWrapperDialogRef {
    this._options.allowedTags = allowedTags;
    return this;
  }

  /**
   * Sets the 'returnFocus' of the modal control.
   *
   * @param returnFocus -if true; the dialog will autoFocus.
   */
  returnFocus(returnFocus: JQuery): LMMessageWrapperDialogRef {
    this._options.returnFocus = returnFocus;
    return this;
  }

  /**
   * Controls the opacity of the background overlay.
   *
   * @param overlayOpacity - The percent between 0 and 1 of opacity to use.
   */
  overlayOpacity(overlayOpacity: number): LMMessageWrapperDialogRef {
    this._options.overlayOpacity = overlayOpacity;
    return this;
  }

  /**
   * Causes this modal instance to become hidden when another modal is displayed over top.
   *
   * @param hideUnderneath - If it should be on top or bottom.
   */
  hideUnderneath(hideUnderneath: boolean): LMMessageWrapperDialogRef {
    this._options.hideUnderneath = hideUnderneath;
    return this;
  }

  /**
   * Causes this modal instance to become hidden when another modal is displayed over top.
   *
   * @param hideUnderneath - If it should be on top or bottom.
   */
  attributes(attributes: Array<object> | object): LMMessageWrapperDialogRef {
    this._options.attributes = attributes;
    return this;
  }

  /**
   * Dialog result property.
   *
   * @param dialogResult - the stored restult of the dialog.
   */
  set dialogResult(dialogResult: any) {
    this._dialogResult = dialogResult;
  }

  get dialogResult(): any {
    return this._dialogResult;
  }

  get uuid(): number {
    return this._uuid;
  }

  private _uuid = 0;

  /**
   * Constructor.
   *
   * @param lmModalOpenService
   */
  constructor(
    private lmModalOpenService: LMModalOpenService,
    private lmContextService: LMContextService
  ) {
    if (this.lmContextService.isUnified()) {
      this._uuid = Date.now();
    }
    this.messageDialog = new SohoMessageRef();

    // default the beforeClose callback if in unified mode, to make sure messages are removed from the
    // open modal/message collection
    if (this.lmContextService.isUnified()) {
      this.messageDialog.beforeClose(this.beforeCloseMethod);
    }
  }

  /**
   * Displays the message.
   *
   * @return the dialog ref.
   */
  open(): LMMessageWrapperDialogRef {
    // Returns the body!
    this.messageDialog.options(this._options);
    if (this.lmContextService.isUnified()) {
      this.lmModalOpenService.addModal(this.uuid, this);
    }
    this.messageDialog.open();
    return this;
  }

  /**
   * Closes the modal dialog, if open.
   *
   * @param dialogResult - optional result - held for the caller.
   */
  close(dialogResult?: any): LMMessageWrapperDialogRef {
    this.dialogResult = dialogResult;

    if (this.messageDialog) {
      this.messageDialog.close();
    }
    return this;
  }

  // ------------------------------------------
  // Event Registration
  // ------------------------------------------

  /**
   * Registers a before open guard.
   *
   * @param eventFn - the function to call before openning the dialog.
   */
  beforeOpen(eventFn: () => boolean): LMMessageWrapperDialogRef {
    if (this.messageDialog) {
      this.messageDialog.beforeOpen(eventFn);
    }
    return this;
  }

  /**
   * Opened Event.
   *
   * This event is fired when the dialog is being opened.
   *
   * @param eventFn - the function to invoke when the dialog is to be opened.
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  opened(eventFn: Function): LMMessageWrapperDialogRef {
    if (this.messageDialog) {
      this.messageDialog.opened(eventFn);
    }
    return this;
  }

  /**
   * Registers a before close guard.
   *
   * @param eventFn - the function to call before closing the dialog.
   */
  beforeClose(eventFn: () => boolean): LMMessageWrapperDialogRef {
    if (this.messageDialog) {
      this.messageDialog.beforeClose(eventFn);
    }
    return this;
  }
}
